import { AbstractControl, FormArray, FormGroup, ValidationErrors } from '@angular/forms';
import { addDays, addMinutes, format, isBefore, parse, startOfDay } from 'date-fns';

/**
 * Shared validation utilities for scheduler components
 */
export class SchedulerValidationUtils {
	/**
	 * Get the minimum allowed start date (7 days from today)
	 */
	static getMinimumStartDate(): Date {
		return startOfDay(addDays(new Date(), 7));
	}

	/**
	 * Check if a date is before the minimum allowed start date
	 */
	static isDateTooEarly(date: Date): boolean {
		return isBefore(date, this.getMinimumStartDate());
	}
}

export class SchedulerValidators {
	/**
	 * Step 1 FormGroup validator - validates the entire step as a unit
	 */
	static step1FormValidator(control: AbstractControl): ValidationErrors | null {
		const formGroup = control as FormGroup;

		const frequency = formGroup.get('frequency')?.value;
		const startDate = formGroup.get('startDate')?.value;
		const timezone = formGroup.get('timeZone')?.value;
		const isAdmin = formGroup.get('isAdmin')?.value;

		const errors: ValidationErrors = {};

		// Frequency validation
		if (!frequency || frequency <= 0) {
			errors['frequencyRequired'] = { message: 'Please select a lesson frequency' };
		}

		// Start date validation
		if (!startDate) {
			errors['startDateRequired'] = { message: 'Please select a start date' };
		} else if (!isAdmin) {
			const inputDate = new Date(startDate.replace(/-/g, '/'));
			const minimumDate = SchedulerValidationUtils.getMinimumStartDate();

			if (isBefore(inputDate, minimumDate)) {
				errors['startDateTooEarly'] = {
					message: 'Start date must be at least 7 days from today',
					minimumDate: minimumDate.toISOString().split('T')[0]
				};
			}
		}

		if (isAdmin && timezone == null) {
			errors['timezoneRequired'] = { message: 'Please select a time zone' };
		}

		// Return null if no errors, otherwise return errors object
		return Object.keys(errors).length > 0 ? errors : null;
	}

	static step2FormValidator(control: AbstractControl): ValidationErrors | null {
		const formArray = control as FormArray;
		const formValue = formArray.value;

		// We need to get frequency from the parent wizard form
		// Look up the form hierarchy to find step1
		let wizardForm = formArray.parent;
		while (wizardForm && !wizardForm.get('step1')) {
			wizardForm = wizardForm.parent;
		}

		if (!wizardForm) {
			return { missingWizardForm: true };
		}

		const step1Group = wizardForm.get('step1') as FormGroup;
		const frequency = step1Group?.get('frequency')?.value as number;

		if (!frequency || frequency <= 0) {
			return { missingFrequency: true };
		}

		// Must have at least 'frequency' number of unique days selected
		const uniqueDays = new Set(formValue.map((x: any) => x.dayIndex));
		const uniqueDayCount = [...uniqueDays].length;

		if (frequency && uniqueDayCount < frequency) {
			const error = {
				invalidScheduleCount: {
					message: `${frequency} hour${frequency > 1 ? 's' : ''} must be selected in order to proceed.`,
					required: frequency,
					selected: formArray.length
				}
			};
			return error;
		}

		return null;
	}

	static step3FormValidator(control: AbstractControl): ValidationErrors | null {
		const formArray = control as FormArray;
		const formValue = formArray.value;

		// We need to get frequency from the parent wizard form
		// Look up the form hierarchy to find step1
		let wizardForm = formArray.parent;
		while (wizardForm && !wizardForm.get('step1')) {
			wizardForm = wizardForm.parent;
		}

		if (!wizardForm) {
			return { missingWizardForm: true };
		}

		const step1Group = wizardForm.get('step1') as FormGroup;
		const frequency = step1Group?.get('frequency')?.value as number;

		if (!frequency || frequency <= 0) {
			return { missingFrequency: true };
		}

		// Must have at least 'frequency' number of unique days selected
		const uniqueDays = new Set(formValue.map((x: any) => x.dayIndex));

		if (frequency && [...uniqueDays].length < frequency) {
			return {
				invalidScheduleCount: {
					message: `Please select ${frequency} schedule(s)`,
					required: frequency,
					selected: formArray.length
				}
			};
		}

		const minAdditionalHours = frequency * 2; // Per https://trello.com/c/GLzoIrry/596-small-bugs-adjustments-to-learner-availability-and-matching

		// The form value is an array of 30 minute blocks
		if ((minAdditionalHours + frequency) * 2 > formValue.length) {
			return {
				invalidScheduleCount: {
					message: `${minAdditionalHours} additional hours must be selected in order to proceed.`,
					required: minAdditionalHours,
					selected: formArray.length
				}
			};
		}

		const marginErrors: any[] = [];
		// Check for any invalid time blocks, 30 mins blocks are not allowed
		formValue.forEach((schedule: any) => {
			function isTimeWithinMargin(x: any) {
				const ahead = format(addMinutes(parse(schedule.timeID, 'HH:mm', new Date()), 30), 'HH:mm');
				const behind = format(addMinutes(parse(schedule.timeID, 'HH:mm', new Date()), -30), 'HH:mm');

				return x.timeID === ahead || x.timeID === behind;
			}

			const possibleBlock = formValue.find((x: any) => x.dayIndex === schedule.dayIndex && x.timeID !== schedule.timeID && isTimeWithinMargin(x));

			if (!possibleBlock) {
				marginErrors.push(schedule);
			}

			return null;
		});

		if (marginErrors.length > 0) {
			return {
				invalidTimeBlock: {
					message: '30 minute time blocks are not allowed'
				}
			};
		}
		return null;
	}

	static step4FormValidator(control: AbstractControl): ValidationErrors | null {
		// TODO: Do we need any validation on step 3?
		return null;
	}
}
